import React from 'react'

import Link from 'next/link'
import { PiCrownSimpleDuotone } from 'react-icons/pi'
import { twMerge } from 'tailwind-merge'

import Button from '@/components/common/Button'
import Popover from '@/components/common/PopoverV2'

import { DEMO_URL } from '@/constants/general'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'

interface PremiumAccessPopoverProps {
  children: React.ReactNode
  open?: boolean
  triggerClassName?: string
  onOpenChange?: (open: boolean) => void
}

export const PremiumAccessContent: React.FC = () => {
  return (
    <div className="flex flex-col gap-2 p-3 max-w-[300px] bg-white dark:bg-primary-900 rounded-md border border-primary-200 dark:border-primary-800 shadow-md">
      <div className="flex items-center justify-center gap-2 text-[13px] font-medium text-primary-900 dark:text-primary-100 w-full py-1 rounded-md bg-primary-100 dark:bg-primary-800">
        <PiCrownSimpleDuotone
          size={15}
          className="text-yellow-600 dark:text-yellow-400"
        />
        <span>Premium Feature</span>
      </div>
      <span className="text-[13px] text-primary-900 dark:text-primary-100">
        Get tailored opportunities, proposal writing tools, AI-powered company
        data management, and everything else you need to win more public sector
        work with a Sweetspot Premium plan.
      </span>
      <Link href={DEMO_URL} target="_blank">
        <Button variant="primary-compact" className="w-full text-[13px]">
          Schedule a Free Demo
        </Button>
      </Link>
    </div>
  )
}

interface BasicAccessContentProps {
  children?: React.ReactNode
  className?: string
}

export const BasicAccessContent: React.FC<BasicAccessContentProps> = ({
  children,
  className,
}) => {
  return (
    <div
      className={twMerge(
        'flex flex-col gap-2 p-3 max-w-[300px] bg-white dark:bg-primary-900 rounded-md border border-primary-200 dark:border-primary-800',
        className,
      )}
    >
      <div className="flex items-center justify-center gap-2 text-[13px] font-medium text-primary-900 dark:text-primary-100 w-full py-1 rounded-md bg-primary-100 dark:bg-primary-800">
        <PiCrownSimpleDuotone
          size={15}
          className="text-yellow-600 dark:text-yellow-400"
        />
        <span>Paid Feature</span>
      </div>
      <div className="text-[13px] text-primary-900 dark:text-primary-100">
        {children || (
          <span>
            Get access to the capture tools you need to win more public sector
            work with a Sweetspot paid plan.
          </span>
        )}
      </div>
      <Link href="/upgrade" target="_blank">
        <Button variant="primary-compact" className="w-full text-[13px]">
          Upgrade
        </Button>
      </Link>
    </div>
  )
}

export const PremiumAccessPopover: React.FC<PremiumAccessPopoverProps> = ({
  children,
  open,
  onOpenChange,
  triggerClassName,
}) => {
  const { canAccessPremium } = useCanAccessPremium()

  const handleOpenAutoFocus = (event: Event) => {
    event.preventDefault()
  }

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Popover.Trigger asChild={!canAccessPremium} className={triggerClassName}>
        {children}
      </Popover.Trigger>
      {!canAccessPremium && (
        <Popover.Content
          className="max-w-[300px] p-0"
          onOpenAutoFocus={handleOpenAutoFocus}
          asChild={true}
        >
          <PremiumAccessContent />
        </Popover.Content>
      )}
    </Popover>
  )
}
